import { Center, Checkbox, Link as ChakraLink } from "@chakra-ui/react";
import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useGetBots } from "shared/api/requests/bots";
import { useGetChannels } from "shared/api/requests/channels";
import { BotsElementT } from "shared/api/types/bots";
import CutText from "shared/components/CutText";
import MainItemMenu from "shared/components/MainItemMenu";
import { LayoutsE, PathsE } from "shared/routes";
import { FilterField } from "shared/types/filters";
import { formatDateToHuman } from "shared/utils/formattings";

export const useGetFilterFields = (): FilterField[] => {
  const { t } = useTranslation();

  return useMemo(() => [
    {
      type: 'async-select',
      isMulti: true,
      name: 'channelIds',
      defaultValue: [],
      placeholder: t('forms.selectChannels'),
      optionsQuery:  useGetChannels,
      label: t('fields.channel', { count: 0 }),
      size: "lg",
      labelName: 'name',
      valueName: 'id',
      fullWidth: true
    },
    {
      type: 'async-select',
      isMulti: true,
      name: 'ids',
      defaultValue: [],
      placeholder: t('forms.selectBots'),
      optionsQuery:  useGetBots,
      // TODO add translations
      label: t("bots.bots"),
      size: "lg",
      labelName: 'name',
      valueName: 'id',
      fullWidth: true
    },
    {
      type: 'date-range',
      name: 'messageAt',
      defaultValue: [],
      // TODO add translations
      label: t('channels.table.messageAt'),
      size: "lg"
    },
    {
      type: 'date-range',
      name: 'createdAt',
      defaultValue: [],
      label: t('subscribers.table.createdAt'),
      size: "lg"
    },
  ], [t]);
}

export const useGetGetBreadcrumbs = () => {
  const { t } = useTranslation();
  return [
    {
      path: PathsE.HOME,
      text: t('general.home'),
    },
  ];
}

interface Params {
  handleActivate(botId: string): void
  handleDeactivate(botId: string): void
  handleDelete(id: string): void
}

export const useGetColumns = ({ handleActivate, handleDeactivate, handleDelete }: Params) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<BotsElementT>();

  return useMemo(
    () => [
      columnHelper.accessor('id', {
        header: t('bots.table.id'),
        id: 'id',
        cell: ({ getValue }) => (
          <CutText fontWeight="700">
            {getValue()}
          </CutText>
        ),
        meta: {
          isSorting: false,
          styles: {
            sizes: {
              width: '90px',
              maxW: '90px',
            }
          }
        }
      }),
      columnHelper.accessor('type', {
        header: t('bots.table.type'),
        id: 'type',
      }),
      columnHelper.accessor('name', {
        cell: ({ row, getValue }) => (
          <Link to={`/${LayoutsE.DASHBOARD}/${PathsE.BOTS}/${row.original.id}`}>
            <ChakraLink color="blue" as="span">{getValue()}</ChakraLink>
          </Link>
        ),
        header: t('bots.table.name'),
        id: 'name',
      }),
      columnHelper.accessor('username', {
        header: t('bots.table.username'),
        id: 'username',
      }),
      columnHelper.accessor('status', {
        header: t('bots.table.status'),
        id: 'status',
      }),
      columnHelper.accessor('isActive', {
        header: t('channels.table.isActive'),
        id: 'isActive',
        meta: {
          styles: {
            sizes: {
              width: "70px"
            }
          }
        },
        cell: ({ row }) => {
          const { id, isActive } = row.original;

          return (
            <Center>
              <Checkbox
                isChecked={isActive}
                onChange={() => handleDeactivate(id)}
                colorScheme="brand"
              />
            </Center>
          );
        },
      }),
      columnHelper.accessor('createdAt', {
        header: t('bots.table.createdAt'),
        id: 'createdAt',
        cell: ({ getValue }) => formatDateToHuman(getValue(), false),
        meta: {
          isNumeric: true,
          styles: {
            sizes: {
              width: '120px'
            }
          }
        }
      }),
      columnHelper.accessor('updatedAt', {
        header: t('bots.table.updatedAt'),
        id: 'updatedAt',
        cell: ({ getValue }) => formatDateToHuman(getValue(), false),
        meta: {
          isNumeric: true,
          styles: {
            sizes: {
              width: '120px'
            }
          }
        }
      }),
      columnHelper.accessor('id', {
        header: '',
        id: 'actions',
        cell: ({ row }) => (
          <MainItemMenu
            data={row.original}
            handleActivate={handleActivate}
            handleDeactivate={handleDeactivate}
            handleDelete={handleDelete}
            viewLink={PathsE.SHOW_BOT}
            editLink={PathsE.UPDATE_BOT}
            pathsParams={{ botId: row.original.id }}
          />
        ),
        meta: {
          styles: {
            sizes: {
              width: '44px'
            }
          }
        }
      }),
    ] as ColumnDef<BotsElementT>[],
    [t, handleActivate, handleDeactivate, handleDelete],
  );
};
